import React, { useMemo, useState } from "react";
import PosterArt from "./PosterArt";
import Wordmark from "./Wordmark";

// YOUR KEEPERS — the evidence behind the taste.
// A Keeper is a work the user actually kept in KEEP ONE. This surface is a
// personal cinematic archive, NOT a watchlist/dashboard: real artwork carries
// the interface, restrained TT accents, near-black stage. The Taste Lens turns
// an abstract signal into the recognizable evidence that produced it:
// SIGNAL -> EVIDENCE -> PATTERN.

const DOMAIN_TABS = [
  { id: "ALL", label: "All" },
  { id: "FILM", label: "Film" },
  { id: "SERIES", label: "Series" },
  { id: "GAME", label: "Games" },
];

function asWork(k) {
  // Adapt a Keeper/mini record into the shape PosterArt expects.
  return { id: k.workId, title: k.title, domain: k.domain, year: k.year, poster: k.poster };
}

function KeeperTile({ k, onOpen }) {
  return (
    <button
      type="button"
      data-testid="keeper-tile"
      onClick={() => onOpen(k)}
      className="group text-left focus:outline-none"
    >
      <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden ring-1 ring-white/10 shadow-[0_8px_28px_rgba(0,0,0,0.5)] group-active:scale-[0.985] transition-transform">
        <PosterArt work={asWork(k)} showMeta={false} />
        {k.keepCount > 1 && (
          <span className="absolute top-1.5 right-1.5 font-mono text-[8.5px] font-bold tracking-[0.12em] uppercase text-bone/95 bg-black/60 backdrop-blur-sm border border-white/20 rounded-full px-1.5 py-[2px]">
            Kept ×{k.keepCount}
          </span>
        )}
      </div>
      <p className="mt-1.5 font-display font-semibold text-[11px] leading-tight text-bone/90 line-clamp-2">
        {k.title}
      </p>
    </button>
  );
}

function KeeperDetail({ k, onClose }) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="keeper-detail"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[440px] max-h-[88dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#0c0d12] border border-white/12 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-4">
          <div className="relative w-28 shrink-0 aspect-[2/3] rounded-xl overflow-hidden ring-1 ring-white/12">
            <PosterArt work={asWork(k)} showMeta={false} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-black text-lg text-bone leading-tight">{k.title}</h3>
            <p className="font-mono text-[9px] tracking-[0.18em] uppercase text-subtle mt-1">
              {k.domain} · {k.year}
            </p>
            <p className="mt-2 font-display font-bold text-[13px] tracking-[0.06em] uppercase"
               style={{ color: "var(--tt-right)" }}>
              Kept {k.keepCount === 1 ? "once" : `×${k.keepCount}`}
            </p>
            {Array.isArray(k.chips) && k.chips.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {k.chips.map((c) => (
                  <span key={c} className="font-mono text-[8px] font-semibold tracking-[0.12em] uppercase text-bone/80 border border-white/15 bg-white/[0.05] rounded-full px-2 py-[2px]">
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {Array.isArray(k.keptOver) && k.keptOver.length > 0 && (
          <div className="mt-5">
            <p className="font-mono text-[10px] font-bold tracking-[0.24em] uppercase text-subtle mb-2.5">
              Kept over
            </p>
            <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1">
              {k.keptOver.map((o) => (
                <div key={o.workId} className="w-16 shrink-0">
                  <div className="relative w-full aspect-[2/3] rounded-md overflow-hidden ring-1 ring-white/10 opacity-90">
                    <PosterArt work={asWork(o)} showMeta={false} />
                  </div>
                  <p className="mt-1 font-body text-[9px] leading-tight text-silver line-clamp-2">{o.title}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-full border border-white/15 bg-white/[0.04] py-3 font-display font-bold uppercase tracking-[0.14em] text-[11px] text-bone/85 hover:bg-white/[0.08]"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function Chip({ active, accent, children, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`shrink-0 font-mono text-[10px] font-bold tracking-[0.16em] uppercase rounded-full px-3.5 py-2 border transition-colors ${
        active
          ? "text-[#05070a] border-transparent"
          : "text-bone/70 border-white/15 bg-white/[0.04] hover:text-bone hover:border-white/30"
      }`}
      style={active ? { background: accent || "var(--tt-bone,#efeae2)" } : undefined}
    >
      {children}
    </button>
  );
}



// A work that merely survived one confrontation isn't equally defining as one
// that's repeatedly, credibly emerging as a real preference. Below this many
// qualified Core Keepers, defaulting to CORE would just show a sparse,
// disappointing grid — ALL (with early-state copy) is the honest default.
export const MIN_CORE_FOR_DEFAULT = 3;

// Pure, unit-testable decision logic — kept outside the component so it can
// be exercised without rendering.
export function defaultStrengthView(coreCount) {
  return coreCount >= MIN_CORE_FOR_DEFAULT ? "CORE" : "ALL";
}

export function filterShelf(keepers, { activeLens, strengthView, domain }) {
  let list = keepers;
  if (activeLens) {
    // The Lens is already a curated, strength-prioritized subset for this
    // signal — the CORE/ALL toggle governs the general wall, not this view.
    const order = activeLens.supportingWorkIds;
    const rank = new Map(order.map((id, i) => [id, i]));
    list = keepers.filter((k) => rank.has(k.workId));
    list = [...list].sort((a, b) => rank.get(a.workId) - rank.get(b.workId));
  } else if (strengthView === "CORE") {
    list = list.filter((k) => k.tier !== "survived");
  }
  if (domain !== "ALL") list = list.filter((k) => k.domain === domain);
  return list;
}

export default function KeepersView({ data, onBack, onPlayMore, initialLensKey = null, backLabel = "Profile" }) {
  const [domain, setDomain] = useState("ALL");
  const [lensKey, setLensKey] = useState(initialLensKey);
  const [selected, setSelected] = useState(null);

  const keepers = data?.keepers || [];
  const lenses = data?.lenses || [];
  const total = data?.total || 0;
  const coreCount = data?.coreCount || 0;
  const [strengthView, setStrengthView] = useState(() => defaultStrengthView(coreCount));
  const activeLens = useMemo(() => lenses.find((l) => l.key === lensKey) || null, [lenses, lensKey]);

  const shelf = useMemo(
    () => filterShelf(keepers, { activeLens, strengthView, domain }),
    [keepers, activeLens, domain, strengthView]
  );

  return (
    <div data-testid="keepers-view" className="relative min-h-[100dvh] w-full max-w-[560px] mx-auto overflow-x-hidden bg-canvas">
      <div className="relative z-10 px-5 pt-[calc(env(safe-area-inset-top)+20px)] pb-24">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Wordmark variant="horizontal" className="h-3.5" />
          <button
            type="button"
            data-testid="keepers-back"
            onClick={onBack}
            className="font-body text-[11px] tracking-[0.18em] uppercase text-white/50 hover:text-white/85"
          >
            ← {backLabel}
          </button>
        </div>
        <div className="mt-3 h-px w-full" style={{ background: "linear-gradient(90deg, var(--tt-left), var(--tt-right))", opacity: 0.7 }} />

        <h1 className="mt-7 font-display font-black uppercase tracking-[-0.01em] text-bone text-3xl">
          {activeLens ? (
            <>Your Keepers <span className="text-bone/45">· {activeLens.label}</span></>
          ) : (
            "Your Keepers"
          )}
        </h1>
        <p className="mt-1.5 font-body text-[13px] text-silver">
          {total > 0 ? "The works that survive your choices." : "The evidence behind your taste."}
        </p>

        {total === 0 ? (
          <div className="mt-16 text-center" data-testid="keepers-empty">
            <p className="font-display font-bold text-bone text-lg">Your first Keepers are taking shape.</p>
            <p className="mt-2 font-body text-[13px] text-silver">Your choices will build this shelf.</p>
            {onPlayMore && (
              <button
                type="button"
                onClick={onPlayMore}
                className="mt-7 rounded-full px-8 py-3.5 font-display font-black uppercase tracking-[0.06em] text-[12px] text-[#05070a]"
                style={{ background: "linear-gradient(90deg, var(--tt-left), var(--tt-right))" }}
              >
                Take a Test
              </button>
            )}
          </div>
        ) : (
          <>
            {/* CORE / ALL — same archive, either every work that's ever
                survived a confrontation, or just the ones with credibly
                repeated support. Doesn't apply inside a Taste Lens, which is
                already a curated, strength-prioritized subset. */}
            {!activeLens && (
              <div className="mt-6 flex gap-2" data-testid="keeper-strength-row">
                <Chip testid="strength-core" active={strengthView === "CORE"} onClick={() => setStrengthView("CORE")}>
                  Core{coreCount > 0 ? ` (${coreCount})` : ""}
                </Chip>
                <Chip testid="strength-all" active={strengthView === "ALL"} onClick={() => setStrengthView("ALL")}>
                  All ({total})
                </Chip>
              </div>
            )}

            {/* Taste Lens */}
            {lenses.length > 0 && (
              <div className="mt-7">
                <p className="font-mono text-[10px] font-bold tracking-[0.24em] uppercase text-subtle mb-2.5">
                  Taste Lens
                </p>
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1" data-testid="taste-lens-row">
                  <Chip active={!lensKey} onClick={() => setLensKey(null)} testid="lens-all">All</Chip>
                  {lenses.map((l) => (
                    <Chip
                      key={l.key}
                      testid={`lens-${l.key}`}
                      active={lensKey === l.key}
                      accent="var(--tt-right)"
                      onClick={() => setLensKey(lensKey === l.key ? null : l.key)}
                    >
                      {l.label}
                    </Chip>
                  ))}
                </div>
                {activeLens?.detail && (
                  <p data-testid="lens-detail" className="mt-3 font-body text-[13px] text-bone/85 leading-snug border-l-2 pl-3" style={{ borderColor: "var(--tt-right)" }}>
                    {activeLens.detail}
                  </p>
                )}
              </div>
            )}

            {/* Domain filter */}
            <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1" data-testid="keeper-domain-row">
              {DOMAIN_TABS.map((d) => (
                <Chip
                  key={d.id}
                  testid={`domain-${d.id}`}
                  active={domain === d.id}
                  onClick={() => setDomain(d.id)}
                >
                  {d.label}
                </Chip>
              ))}
            </div>

            {/* Poster shelf */}
            {shelf.length === 0 ? (
              <p className="mt-10 text-center font-body text-[13px] text-silver" data-testid="keeper-shelf-empty">
                {!activeLens && strengthView === "CORE"
                  ? "Your core Keepers are still taking shape."
                  : "No Keepers in this view yet."}
              </p>
            ) : (
              <div className="mt-6 grid grid-cols-3 gap-x-3 gap-y-5" data-testid="keeper-shelf">
                {shelf.map((k) => (
                  <KeeperTile key={k.workId} k={k} onOpen={setSelected} />
                ))}
              </div>
            )}

            <p className="mt-9 text-center font-body text-[12px] text-white/45" data-testid="keepers-total-line">
              {total} {total === 1 ? "work has" : "works have"} survived your choices.
            </p>
            {coreCount > 0 && (
              <p className="mt-1 text-center font-body text-[12px] text-white/45" data-testid="keepers-core-line">
                {coreCount} {coreCount === 1 ? "is" : "are"} emerging as core Keepers.
              </p>
            )}
            {onPlayMore && (
              <button
                type="button"
                data-testid="keepers-play-more"
                onClick={onPlayMore}
                className="mt-4 w-full text-center font-body font-medium text-[11px] tracking-[0.2em] uppercase text-white/45 hover:text-white/75"
              >
                Take another Test to sharpen your Keepers
              </button>
            )}
          </>
        )}
      </div>

      {selected && <KeeperDetail k={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
